<template>
  <bg-card class="program-target">
    <bg-text size="title-5" class="bg-u-text-flirt-500">
      3. Target Program
    </bg-text>

    <bg-divider class="bg-u-my-md" />

    <div class="program-target__options">
      <option-card
        v-for="option in TARGET_OPTIONS"
        :key="option.value"
        :description="option.description"
        :active="value.targetType === option.value"
        :disabled="disabled"
        @click="emitUpdate('targetType', option.value)"
      >
        <bg-text size="body-2" class="bg-u-mb-xxs">
          <strong>{{ option.label }}</strong>
        </bg-text>
      </option-card>
    </div>

    <template v-if="value.targetType === PROGRAM_TARGET.SPECIFIC">
      <bg-uploader
        v-if="!disabled"
        class="program-target__uploader bg-u-mt-md"
        accept=".csv"
        :progress="csvFile.file ? 100 : 0"
        :is-error="!!csvFile.error"
        :message="csvFile.error"
        :is-progress-delete="csvFile.isDeleting"
        :is-success-delete="csvFile.deleted"
        @upload="handleCsvUpload"
        @delete="handleCsvDelete"
      >
        <bg-icon name="upload" />
        <bg-text size="title-5" class="bg-u-mt-xs">
          Upload file CSV {{ hasExistingCsv ? 'baru' : '' }} di sini
        </bg-text>

        <template #preview>
          <bg-text
            size="label-1"
            class="program-target__csv-preview-link"
            role="button"
            @click.native="isCsvPreviewOpen = true"
          >
            Preview File
          </bg-text>
        </template>
      </bg-uploader>

      <div
        v-if="hasExistingCsv"
        class="program-target__existing-csv bg-u-mt-md"
      >
        <bg-text size="body-2" class="bg-u-text-neutral-700">
          File CSV saat ini:
        </bg-text>

        <bg-link
          :href="csvFile.url"
          target="_blank"
          rel="noopener"
          variant="medium"
        >
          Unduh File
        </bg-link>
      </div>
    </template>

    <csv-preview-modal v-model="isCsvPreviewOpen" :file="csvFile.file" />
  </bg-card>
</template>

<script>
import { ref, computed, nextTick } from '@vue/composition-api';
import {
  BgCard,
  BgText,
  BgDivider,
  BgIcon,
  BgUploader,
  BgLink,
} from 'bangul-vue';

import OptionCard from '@admin_molecules/OptionCard';
import CsvPreviewModal from '@admin_molecules/CsvPreviewModal';

import {
  PROGRAM_TARGET,
  PROGRAM_TARGET_LABEL,
} from '@admin_pages/OwnerProgram/constants';

const MAX_CSV_SIZE = 2 * 1024 * 1024;

const validateCsv = file => {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return 'Format file harus CSV.';
  }

  if (file.size > MAX_CSV_SIZE) {
    return 'Ukuran file maksimal 2 MB.';
  }

  return '';
};

const TARGET_OPTIONS = [
  {
    value: PROGRAM_TARGET.ALL,
    label: PROGRAM_TARGET_LABEL[PROGRAM_TARGET.ALL],
    description: 'Program tampil di semua Dashboard Singgahsini pemilik',
  },
  {
    value: PROGRAM_TARGET.SPECIFIC,
    label: PROGRAM_TARGET_LABEL[PROGRAM_TARGET.SPECIFIC],
    description: 'Hanya tampil di Dashboard pemilik tertentu',
  },
];

export default {
  name: 'ProgramTarget',

  components: {
    BgCard,
    BgText,
    BgDivider,
    BgIcon,
    BgUploader,
    BgLink,
    OptionCard,
    CsvPreviewModal,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    // Read-only mode (e.g. an ended program).
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const csvFile = computed(() => ({
      file: null,
      url: null,
      isDeleting: false,
      deleted: false,
      error: '',
      ...props.value.csvFile,
    }));

    const hasExistingCsv = computed(
      () => !!csvFile.value.url && !csvFile.value.file && !csvFile.value.deleted
    );

    const emitUpdate = (key, val) => {
      emit('input', { ...props.value, [key]: val });
    };

    const handleCsvUpload = file => {
      const error = validateCsv(file);

      emitUpdate('csvFile', {
        file: error ? null : file,
        isDeleting: false,
        deleted: false,
        error,
      });
    };

    const handleCsvDelete = async () => {
      /*
       * BgUploader only resets its preview to the upload trigger when
       * isProgressDelete transitions true -> false, so pulse it across a
       * tick. Without the gap Vue batches both states and the reset is missed.
       */
      emitUpdate('csvFile', { ...csvFile.value, isDeleting: true });

      await nextTick();

      emitUpdate('csvFile', {
        file: null,
        isDeleting: false,
        deleted: true,
        error: '',
      });
    };

    const isCsvPreviewOpen = ref(false);

    return {
      PROGRAM_TARGET,
      TARGET_OPTIONS,
      csvFile,
      hasExistingCsv,
      emitUpdate,
      handleCsvUpload,
      handleCsvDelete,
      isCsvPreviewOpen,
    };
  },
};
</script>

<style lang="scss" scoped src="./ProgramTarget.scss" />
